// engine/particles.ts — one fixed-size pool, no allocation after boot.

const MAX = 400

type Shape = 0 | 1 // 0 = square chunk, 1 = round spark

class P {
  x = 0
  y = 0
  vx = 0
  vy = 0
  life = 0
  maxLife = 1
  size = 2
  drag = 0.9
  grav = 0
  color = '#fff'
  shape: Shape = 0
  spin = 0
  fade = true
}

const pool: P[] = []
for (let i = 0; i < MAX; i++) pool.push(new P())
let count = 0

export function clearParticles(): void {
  count = 0
}

export function particleCount(): number {
  return count
}

function take(): P | null {
  if (count >= MAX) {
    // Recycle the oldest slot rather than dropping the newest effect.
    const p = pool[0]
    pool[0] = pool[count - 1]
    pool[count - 1] = p
    p.life = 0
    return p
  }
  return pool[count++]
}

export interface EmitOpts {
  count?: number
  speed?: number
  speedVar?: number
  life?: number
  lifeVar?: number
  size?: number
  sizeVar?: number
  color?: string
  drag?: number
  grav?: number
  shape?: Shape
  angle?: number
  spread?: number
  fade?: boolean
}

export function emit(x: number, y: number, o: EmitOpts = {}): void {
  const n = o.count ?? 8
  const baseAngle = o.angle ?? 0
  const spread = o.spread ?? Math.PI * 2
  for (let i = 0; i < n; i++) {
    const p = take()
    if (!p) return
    const a = baseAngle + (Math.random() - 0.5) * spread
    const sp = (o.speed ?? 90) + (Math.random() - 0.5) * (o.speedVar ?? 60)
    p.x = x
    p.y = y
    p.vx = Math.cos(a) * sp
    p.vy = Math.sin(a) * sp
    p.maxLife = Math.max(0.05, (o.life ?? 0.35) + (Math.random() - 0.5) * (o.lifeVar ?? 0.15))
    p.life = p.maxLife
    p.size = Math.max(1, (o.size ?? 3) + (Math.random() - 0.5) * (o.sizeVar ?? 1.5))
    p.color = o.color ?? '#ffffff'
    p.drag = o.drag ?? 0.9
    p.grav = o.grav ?? 0
    p.shape = o.shape ?? 0
    p.fade = o.fade !== false
  }
}

export function updateParticles(dt: number): void {
  for (let i = count - 1; i >= 0; i--) {
    const p = pool[i]
    p.life -= dt
    if (p.life <= 0) {
      const last = pool[count - 1]
      pool[count - 1] = p
      pool[i] = last
      count--
      continue
    }
    const d = Math.pow(p.drag, dt * 60)
    p.vx *= d
    p.vy *= d
    p.vy += p.grav * dt
    p.x += p.vx * dt
    p.y += p.vy * dt
  }
}

export function renderParticles(ctx: CanvasRenderingContext2D): void {
  for (let i = 0; i < count; i++) {
    const p = pool[i]
    const t = p.life / p.maxLife
    ctx.globalAlpha = p.fade ? Math.max(0, Math.min(1, t)) : 1
    ctx.fillStyle = p.color
    const s = p.size * (p.fade ? 0.4 + 0.6 * t : 1)
    if (p.shape === 1) {
      ctx.beginPath()
      ctx.arc(p.x, p.y, s * 0.5, 0, Math.PI * 2)
      ctx.fill()
    } else {
      ctx.fillRect(Math.round(p.x - s / 2), Math.round(p.y - s / 2), Math.round(s), Math.round(s))
    }
  }
  ctx.globalAlpha = 1
}
